
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Select,
  Pagination,
  FormControl,
} from "@mui/material";
import { GridLegacy as Grid } from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import BoxHeader from "../../Components/Common/Box/Boxheader";
import SearchandExport from "../../Components/Common/SearchandExport/SearchandExport";
import ActionIcon from "../../Assets/images/png/Action.png";
import Icon from "../../Assets/images/svg/Icons/CategorieIcon.png";
import SuccessDialog from "../../Components/Common/SuccesMessages/SuccessDialog";
import { useSuccessDialog } from "../../Components/Common/SuccesMessages/SuccessDialog";
import { GetApiMethod, PostApiMethod, PatchApiMethod, DeleteApiMethod } from "../../Utils/ApiService";
import { Formik, Form } from "formik";
import { customerSchema } from "../../Components/Validation";
import ErrorMessage from "../../Components/Common/ErrorMessages/ErrorMessage";

const CustomerManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);

  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null);
  const [isSingleViewTable, setIsSingleViewTable] = useState(false);
  const [showCustomerList, setShowCustomerList] = useState<boolean>(true);
  const isFetched = useRef(false);
  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const [currentCustomer, setCurrentCustomer] = useState<any | null>(null);
  const { open: successOpen, message, showSuccess, closeSuccess } = useSuccessDialog();
  const [stateOptions, setStateOptions] = useState<string[]>([]);
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const isMounted = React.useRef(false);
  const [formValues, setFormValues] = useState({
    customerName: "",
    email: "",
    phone: "",
    address: "",
    state: "",
    city: "",
    pincode: "",
  });

  // Fetch all customers
  const fetchCustomers = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await GetApiMethod(
        `customer?page=${page}&limit=${itemsPerPage}&search=${search}&_=${Date.now()}`
      );
      const records = response?.data?.data?.records || [];
      setCustomers(records);
      setFilteredData(records);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };
 useEffect(() => {
  if (isFetched.current) return; 
  isFetched.current = true;

  fetchCustomers(currentPage, searchQuery);
}, [currentPage, searchQuery]);
  // Fetch single customer by ID
  const fetchCustomerById = async (custId: string) => {
    setLoading(true);
    try {
      const res = await GetApiMethod(`customer/${custId}`);
      const customer = res?.data?.data?.customer;

      if (customer) {
        setCurrentCustomer(customer);
        setCustomers([customer]);
        setFilteredData([customer]);
      } else {
        setCurrentCustomer(null);
      }
    } catch (error) {
      console.error("Fetch error", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchStates = async () => {
    try {
      const res = await GetApiMethod("state-city/state");
      const records = res?.data?.data?.records;

      if (Array.isArray(records)) {
        const stateNames = records.map((r: any) => r.name ?? "");
        setStateOptions(stateNames);
      } else {
        setStateOptions([]);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      setStateOptions([]);
    }
  };

  useEffect(() => {
    if(!isMounted.current){
    fetchStates();
    isMounted.current=true;
    }
  }, []);
  const fetchAllCities = async () => {
    try {
      const res = await GetApiMethod("state-city/city");
      const records = res?.data?.data?.records;

      if (Array.isArray(records)) {
        setCityOptions(records.map((c: any) => c.name)); // all cities
      } else {
        setCityOptions([]);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCityOptions([]);
    }
  };

  useEffect(() => {
    fetchAllCities();
  }, []);

  useEffect(() => {
    if (formValues.state) {
      setFormValues(prev => ({ ...prev, city: "" })); // reset city
    } else {
      setCityOptions([]);
    }
  }, [formValues.state]);
  useEffect(() => {
    if (editingCustomerId && currentCustomer) {
      setFormValues({
        customerName: currentCustomer.name || "",
        email: currentCustomer.email || "",
        phone: currentCustomer.phone || "",
        address: currentCustomer.address || "",
        state: currentCustomer.state || "",
        city: currentCustomer.city || "",
        pincode: currentCustomer.pincode || "",
      });
    }
  }, [editingCustomerId, currentCustomer]);


  // Delete customer
  const deleteCustomer = async (custId: string) => {
    setLoading(true);
    try {
      await DeleteApiMethod(`customer/${custId}`);
      setCustomers(prev => prev.filter(c => c.id !== custId));
      setFilteredData(prev => prev.filter(c => c.id !== custId));
      showSuccess("Customer deleted successfully!");
    } catch (error) {
      console.error("Error deleting customer:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  // Search filter
  useEffect(() => {
    if (searchQuery && showCustomerList) {
      const results = customers.filter(c =>
        c.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredData(results);
    } else {
      setFilteredData(customers);
    }
  }, [searchQuery, customers, showCustomerList]);

  // FIX 1: Handle view/edit/add mode based on route
  useEffect(() => {
    const path = location.pathname;

    if (path.endsWith("/add")) {
      setShowCustomerList(false);
      setIsSingleViewTable(false);
      setEditingCustomerId(null);
      setFormValues({
        customerName: "",
        email: "",
        phone: "",
        address: "",
        state: "",
        city: "",
        pincode: "",
      });
    } else if (path.endsWith("/edit") && id) {
      setShowCustomerList(false);
      setIsSingleViewTable(false);
      setEditingCustomerId(id);
    } else if (id) {
      setShowCustomerList(false);
      setIsSingleViewTable(true);
      setEditingCustomerId(null);
    } else {
      setShowCustomerList(true);
      setIsSingleViewTable(false);
      setEditingCustomerId(null);
    }
  }, [location.pathname, id]);
  useEffect(() => {
    if (id) {
      fetchCustomerById(id);
    }
  }, [id]);


  // FIX 2: Separate useEffect to handle fetch by ID
  useEffect(() => {
    if (id) {
      const path = location.pathname;

      if (path.endsWith("/edit")) {
        setShowCustomerList(false);
        setIsSingleViewTable(false);
        setEditingCustomerId(id);
      } else {
        setShowCustomerList(false);
        setIsSingleViewTable(true);
        setEditingCustomerId(null);
      }

      fetchCustomerById(id); // Always runs when id changes
    }
  }, [id]);

  // Reset form and go back to list
  const resetForm = () => {
    setEditingCustomerId(null);
    setFormValues({
      customerName: "",
      email: "",
      phone: "",
      address: "",
      state: "",
      city: "",
      pincode: "",
    });
    setShowCustomerList(true);
    navigate("/customers");
  };

  // Submit add/edit customer
  const handleSubmitCustomer = async (values: typeof formValues) => {
    const payload = {
      name: values.customerName,
      email: values.email,
      phone: values.phone,
      address: values.address,
      state: values.state,
      city: values.city,
      pincode: values.pincode,
    };

    try {
      setLoading(true);
      if (editingCustomerId) {
        await PatchApiMethod(`customer/${editingCustomerId}`, payload);
        showSuccess("Customer updated successfully!");
      } else {
        await PostApiMethod("customer", payload);
        showSuccess("Customer added successfully!");
      }
      fetchCustomers();
      resetForm();
    } catch (error) {
      console.error("Error saving customer:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  return (
    <AuthorizedLayout>
      <Box sx={{ px: 2, py: 3, fontFamily: "Poppins, Semibold" }}>
        {loading && (
          <Box className="loading-overlay">
            <CircularProgress size={50} thickness={5} />
          </Box>
        )}

        <Card className="card-layout">
          <BoxHeader
            dashboard_name="Customer Management"
            image={Icon}
            button_show={true}
            button_name="New Customer"
            onAdd={() => navigate("/customers/add")}
            tabs={["All Customers"]}
            activeTab={showCustomerList ? 0 : undefined}
            onTabChange={() => navigate("/customers")}
          />

          {!showCustomerList ? (
            isSingleViewTable ? (
              // === Display single customer as a table row ===
              <>
                <SearchandExport
                  searchPlaceholder="Search customer..."
                  onSearchChange={setSearchQuery}
                />

                <Table className="table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Total Bookings</TableCell>
                      <TableCell>Total Sales</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.length > 0 ? (
                      filteredData.map(c => (
                        <TableRow key={c.id}>
                          <TableCell>{c.name}</TableCell>
                          <TableCell>{c.email}</TableCell>
                          <TableCell>{c.phone}</TableCell>
                          <TableCell>{c.totalBookings}</TableCell>
                          <TableCell>{c.totalSales}</TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Typography className="table-cell action">Action</Typography>
                              <IconButton
                                onClick={(e) => c.id && handleMenuOpen(e, c.id)}
                              >
                                <img src={ActionIcon} alt="action" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No data found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                {/* Optionally: you can include pagination, but for single record it’s not necessary */}
              </>
            ) : (
              // === Edit / Add form ===
              <Formik
                enableReinitialize
                initialValues={formValues}
                validationSchema={customerSchema}
                onSubmit={handleSubmitCustomer}
              >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                  <Form>
                    <Typography variant="h6" className="form-header">
                      {editingCustomerId ? "Edit Customer" : "Add New Customer"}
                    </Typography>
                    <Grid container spacing={3} mt={1}>
                      {/* Name */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography className="form-input">Customer Name</Typography>
                        <TextField
                          className="form-input"
                          name="customerName"
                          fullWidth
                          variant="standard"
                          placeholder="Type here"
                          value={values.customerName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.customerName && Boolean(errors.customerName)}
                        />
                        <ErrorMessage touched={touched.customerName} error={errors.customerName} />
                      </Grid>

                      {/* Email */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography className="form-input">Email ID</Typography>
                        <TextField
                          className="form-input"
                          name="email"
                          fullWidth
                          variant="standard"
                          placeholder="Type here"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.email && Boolean(errors.email)}
                        />
                        <ErrorMessage touched={touched.email} error={errors.email} />
                      </Grid>

                      {/* Phone */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography className="form-input">Phone Number</Typography>
                        <TextField
                          className="form-input"
                          name="phone"
                          fullWidth
                          variant="standard"
                          placeholder="Type here"
                          value={values.phone}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d{0,10}$/.test(val)) {
                              handleChange(e);
                            }
                          }}
                          onBlur={handleBlur}
                          error={touched.phone && Boolean(errors.phone)}
                        />
                        <ErrorMessage touched={touched.phone} error={errors.phone} />
                      </Grid>

                      {/* Address */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography className="form-input">Address</Typography>
                        <TextField
                          className="form-input"
                          name="address"
                          fullWidth
                          variant="standard"
                          placeholder="Type here"
                          value={values.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.address && Boolean(errors.address)}
                        />
                        <ErrorMessage touched={touched.address} error={errors.address} />
                      </Grid>

                      {/* State */}
                      {/* State */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography className="form-input">State</Typography>
                        <FormControl fullWidth variant="standard" error={touched.state && Boolean(errors.state)}>
                          <Select
                            className="form-input"
                            name="state"
                            value={values.state}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span style={{ color: "#9e9e9e" }}>Select a state</span>; // dim placeholder
                              }
                              return selected; // show selected value normally
                            }}
                          >
                            {stateOptions.map((s) => (
                              <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                          </Select>
                          <ErrorMessage touched={touched.state} error={errors.state} />
                        </FormControl>
                      </Grid>

                      {/* City */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography className="form-input">City</Typography>
                        <FormControl fullWidth variant="standard" error={touched.city && Boolean(errors.city)}>
                          <Select
                            className="form-input"
                            name="city"
                            value={values.city}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span style={{ color: "#9e9e9e" }}>Select a city</span>; // dim placeholder
                              }
                              return selected;
                            }}
                          >
                            {cityOptions.map((c) => (
                              <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                          </Select>
                          <ErrorMessage touched={touched.city} error={errors.city} />
                        </FormControl>
                      </Grid>

                      {/* Pincode */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography className="form-input">Pincode</Typography>
                        <TextField
                          className="form-input"
                          name="pincode"
                          fullWidth
                          variant="standard"
                          placeholder="Type here"
                          value={values.pincode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.pincode && Boolean(errors.pincode)}
                          helperText={<ErrorMessage touched={touched.pincode} error={errors.pincode} />}
                        />
                      </Grid>
                    </Grid>

                    <Box display="flex" gap={2} mt={3}>
                      <Button className="btn-cancel" variant="outlined" onClick={resetForm}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" className="btn-next">
                        {editingCustomerId ? "Update" : "Add"}
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            )
          ) : (
            // === List mode ===
            <>
              <SearchandExport
                searchPlaceholder="Search customer..."
                onSearchChange={setSearchQuery}
              />

              <Table className="table">
                <TableHead>
                  <TableRow>
                    <TableCell>Customer Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Total Bookings</TableCell>
                    <TableCell>Total Sales</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map(c => (
                      <TableRow key={c.id}>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>{c.email}</TableCell>
                        <TableCell>{c.phone}</TableCell>
                        <TableCell>{c.totalBookings}</TableCell>
                        <TableCell>{c.totalSales}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Typography className="table-cell action">Action</Typography>
                            <IconButton onClick={(e) => handleMenuOpen(e, c.id)}>
                              <img src={ActionIcon} alt="action" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No results found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {totalPages > 0 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, value) => {
                      setCurrentPage(value);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          // PaperProps={{ className: "menu-paper" }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                if (menuRowId) navigate(`/customer/${menuRowId}`);
              }}
              className="view-menu-item"
            >
              View
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                if (menuRowId) navigate(`/customer/${menuRowId}/edit`);
              }}
              className="edit-menu-item"
            >
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (menuRowId) deleteCustomer(menuRowId);
                handleMenuClose();
              }}
              className="delete-menu-item"
            >
              Delete
            </MenuItem>
          </Menu>
        </Card>

        <SuccessDialog open={successOpen} onClose={closeSuccess} message={message} />
      </Box>
    </AuthorizedLayout>
  );
};

export default CustomerManagement;
